'use client';

import { c as _c } from "react/compiler-runtime";
import { type DragEvent, type SyntheticEvent, useCallback } from 'react';
import { Button } from '@/app/components/button';
import { IconArrowBack } from '@/app/components/icons/ui-actions/icon-arrow-back';
import { IconArrowForward } from '@/app/components/icons/ui-actions/icon-arrow-forward';
import { usePrevNextSlider } from '@/app/components/sliders/hooks/usePrevNextSlider';
import type { SpartanProductFeatureListProps } from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/feature-list/types';
import { useFeatureParallax } from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/feature-list/useFeatureParallax';
import { SpartanProductFeatureMediaImage } from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/feature-media';
import { getSpartanProductFeatureVideoPoster, getSpartanProductFeatureVideoSource, SPARTAN_PRODUCT_FEATURE_CAROUSEL_IMAGE_CLASS_NAME, SPARTAN_PRODUCT_FEATURE_CAROUSEL_MAIN_SIZES } from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/media';
import styles from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/styles.module.css';
import { SPARTAN_PRODUCT_ICON_BUTTON_CLASS_NAME } from '@/app/lcv/components/sections/spartan/products/shared/icon-button';
import { SpartanInViewVideo } from '@/app/lcv/components/sections/spartan/products/shared/in-view-video';
import { getSpartanRevealElementProps, SPARTAN_REVEAL_PRESET, SPARTAN_REVEAL_ROLE } from '@/app/lcv/components/sections/spartan/products/shared/reveal';
import { SPARTAN_PRODUCTS_EVENT_NAME } from '@/app/lcv/components/sections/spartan/products/shared/tracking';
import arrowStyles from '@/app/lcv/components/sections/spartan/shared/arrow-motion.module.css';
import { SPARTAN_SLIDER_NAV_LABELS } from '@/app/lcv/components/sections/spartan/shared/slider-nav/constants';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { stripHtmlTags } from '@/app/utils/format';
const FEATURE_NAV_WRAP_CLASS_NAME = 'center-y property-opacity-translate absolute z-10 hidden duration-400 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none lg:pointer-fine:block lg:pointer-fine:pointer-events-none lg:pointer-fine:opacity-0';
export const SpartanProductFeatureList = t0 => {
  const $ = _c(78);
  const {
    features,
    tracking,
    dataGASection,
    selectedIndex,
    emblaApi,
    emblaRef,
    videoControlRefs,
    onImageMotionRef,
    onPrimaryLayerRef,
    onSecondaryImageSettled,
    onImageSwipeProgress,
    isParallaxEnabled,
    isNearViewport,
    canSelectPrevious,
    canSelectNext,
    isVideoExternallyPaused,
    videoStartMode,
    videoVisibilityThreshold,
    onCancelAutoAdvance,
    onVideoProgress,
    onVideoPlaying,
    onVideoPause,
    onVideoWaiting,
    onVideoSeeking,
    onVideoSeeked,
    onVideoError,
    onVideoEnded,
    onImageSettled,
    shouldPreloadVideo
  } = t0;
  const {
    setStageRef,
    setImageRef,
    setSecondaryImageRef
  } = useFeatureParallax(emblaApi, isParallaxEnabled, onImageSwipeProgress);
  const handleNativeDragStart = _temp;
  let t1;
  if ($[0] !== onImageSettled) {
    t1 = (feature, event_0) => {
      const image = event_0.currentTarget;
      if (typeof image.decode !== "function") {
        onImageSettled(feature);
        return;
      }
      image.decode().then(() => onImageSettled(feature), () => onImageSettled(feature));
    };
    $[0] = onImageSettled;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleImageLoad = t1;
  const {
    onPrevButtonClick,
    onNextButtonClick,
    prevBtnDisabled,
    nextBtnDisabled
  } = usePrevNextSlider(emblaApi, onCancelAutoAdvance);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getSpartanRevealElementProps({
      preset: SPARTAN_REVEAL_PRESET.gallery,
      role: SPARTAN_REVEAL_ROLE.galleryMain
    });
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("group relative overflow-hidden rounded-lg", styles.roundedClip);
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== tracking.featureList) {
    t4 = createDataAttributes({
      test: tracking.featureList
    });
    $[4] = tracking.featureList;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== features || $[7] !== handleImageLoad || $[8] !== isNearViewport || $[9] !== isParallaxEnabled || $[10] !== isVideoExternallyPaused || $[11] !== onImageMotionRef || $[12] !== onImageSettled || $[13] !== onPrimaryLayerRef || $[14] !== onSecondaryImageSettled || $[15] !== onVideoEnded || $[16] !== onVideoError || $[17] !== onVideoPause || $[18] !== onVideoPlaying || $[19] !== onVideoProgress || $[20] !== onVideoSeeked || $[21] !== onVideoSeeking || $[22] !== onVideoWaiting || $[23] !== selectedIndex || $[24] !== setImageRef || $[25] !== setSecondaryImageRef || $[26] !== setStageRef || $[27] !== shouldPreloadVideo || $[28] !== tracking || $[29] !== videoControlRefs || $[30] !== videoStartMode || $[31] !== videoVisibilityThreshold) {
    let t6;
    if ($[33] !== handleImageLoad || $[34] !== isNearViewport || $[35] !== isParallaxEnabled || $[36] !== isVideoExternallyPaused || $[37] !== onImageMotionRef || $[38] !== onImageSettled || $[39] !== onPrimaryLayerRef || $[40] !== onSecondaryImageSettled || $[41] !== onVideoEnded || $[42] !== onVideoError || $[43] !== onVideoPause || $[44] !== onVideoPlaying || $[45] !== onVideoProgress || $[46] !== onVideoSeeked || $[47] !== onVideoSeeking || $[48] !== onVideoWaiting || $[49] !== selectedIndex || $[50] !== setImageRef || $[51] !== setSecondaryImageRef || $[52] !== setStageRef || $[53] !== shouldPreloadVideo || $[54] !== tracking || $[55] !== videoControlRefs || $[56] !== videoStartMode || $[57] !== videoVisibilityThreshold) {
      t6 = (feature_0, index) => <li key={[feature_0.title, index].join("-")} className="relative min-w-0 shrink-0 grow-0 basis-full overflow-hidden" aria-hidden={index !== selectedIndex} {...createDataAttributes({
        test: tracking.feature(index),
        active: index === selectedIndex ? "true" : null
      })}>{!!feature_0.video?.desktop.src ? <SpartanInViewVideo ref={control => {
          videoControlRefs.current[index] = control;
        }} src={getSpartanProductFeatureVideoSource(feature_0.video)} image={{
          src: feature_0.image.desktop.src,
          alt: stripHtmlTags(feature_0.title)
        }} poster={getSpartanProductFeatureVideoPoster(feature_0.image, stripHtmlTags(feature_0.title))} index={index} active={index === selectedIndex} sizes={SPARTAN_PRODUCT_FEATURE_CAROUSEL_MAIN_SIZES} preload={shouldPreloadVideo(index) ? "auto" : "none"} startMode={videoStartMode} visibilityThreshold={videoVisibilityThreshold} paused={isVideoExternallyPaused || index !== selectedIndex} wrapperClassName={cn(SPARTAN_PRODUCT_FEATURE_CAROUSEL_IMAGE_CLASS_NAME, "overflow-hidden")} inViewOptions={{
          rootMargin: "0px",
          triggerOnce: false
        }} onProgress={(currentTime, duration) => onVideoProgress(index, currentTime, duration)} onPlaying={() => onVideoPlaying(index)} onPause={() => onVideoPause(index)} onWaiting={() => onVideoWaiting(index)} onSeeking={() => onVideoSeeking(index)} onSeeked={() => onVideoSeeked(index)} onError={() => onVideoError(index)} onPlayRejected={() => onVideoError(index)} onVideoEnded={() => onVideoEnded(index)} testId={tracking.featureMedia(index)} /> : <div className={cn({
          [SPARTAN_PRODUCT_FEATURE_CAROUSEL_IMAGE_CLASS_NAME]: isParallaxEnabled
        })}><div ref={stage => setStageRef(index, stage)} className={cn({
            [styles.parallaxStage]: isParallaxEnabled
          })} data-spartan-feature-motion={isParallaxEnabled ? "parallax" : undefined}><div ref={layer => onPrimaryLayerRef(index, layer)} className={styles.primaryImageLayer}><SpartanProductFeatureMediaImage image={feature_0.image} alt={feature_0.title} testId={tracking.featureMedia(index)} loading={isNearViewport ? "eager" : "lazy"} fetchPriority={isNearViewport && index !== selectedIndex ? "low" : undefined} className={isParallaxEnabled ? styles.parallaxMedia : undefined} imgClassName={styles.imageMotion} imgRef={image_0 => {
                setImageRef(index, image_0);
                onImageMotionRef(index, image_0);
              }} onLoad={event_1 => handleImageLoad(feature_0, event_1)} onError={() => onImageSettled(feature_0)} /></div>{!!feature_0.secondaryImage && <div className={styles.secondaryImageLayer} aria-hidden="true"><SpartanProductFeatureMediaImage image={feature_0.secondaryImage} alt="" loading={isNearViewport ? "eager" : "lazy"} fetchPriority={index === selectedIndex ? undefined : "low"} fade={false} className={isParallaxEnabled ? styles.parallaxMedia : undefined} imgClassName={styles.imageMotion} imgRef={image_1 => {
                setSecondaryImageRef(index, image_1);
                onImageMotionRef(index, image_1, true);
              }} onLoad={event_2 => {
                const image_2 = event_2.currentTarget;
                if (typeof image_2.decode !== "function") {
                  onSecondaryImageSettled(index, true);
                  return;
                }
                image_2.decode().then(() => onSecondaryImageSettled(index, true), () => onSecondaryImageSettled(index, false));
              }} onError={() => onSecondaryImageSettled(index, false)} /></div>}</div></div>}</li>;
      $[33] = handleImageLoad;
      $[34] = isNearViewport;
      $[35] = isParallaxEnabled;
      $[36] = isVideoExternallyPaused;
      $[37] = onImageMotionRef;
      $[38] = onImageSettled;
      $[39] = onPrimaryLayerRef;
      $[40] = onSecondaryImageSettled;
      $[41] = onVideoEnded;
      $[42] = onVideoError;
      $[43] = onVideoPause;
      $[44] = onVideoPlaying;
      $[45] = onVideoProgress;
      $[46] = onVideoSeeked;
      $[47] = onVideoSeeking;
      $[48] = onVideoWaiting;
      $[49] = selectedIndex;
      $[50] = setImageRef;
      $[51] = setSecondaryImageRef;
      $[52] = setStageRef;
      $[53] = shouldPreloadVideo;
      $[54] = tracking;
      $[55] = videoControlRefs;
      $[56] = videoStartMode;
      $[57] = videoVisibilityThreshold;
      $[58] = t6;
    } else {
      t6 = $[58];
    }
    t5 = features.map(t6);
    $[6] = features;
    $[7] = handleImageLoad;
    $[8] = isNearViewport;
    $[9] = isParallaxEnabled;
    $[10] = isVideoExternallyPaused;
    $[11] = onImageMotionRef;
    $[12] = onImageSettled;
    $[13] = onPrimaryLayerRef;
    $[14] = onSecondaryImageSettled;
    $[15] = onVideoEnded;
    $[16] = onVideoError;
    $[17] = onVideoPause;
    $[18] = onVideoPlaying;
    $[19] = onVideoProgress;
    $[20] = onVideoSeeked;
    $[21] = onVideoSeeking;
    $[22] = onVideoWaiting;
    $[23] = selectedIndex;
    $[24] = setImageRef;
    $[25] = setSecondaryImageRef;
    $[26] = setStageRef;
    $[27] = shouldPreloadVideo;
    $[28] = tracking;
    $[29] = videoControlRefs;
    $[30] = videoStartMode;
    $[31] = videoVisibilityThreshold;
    $[32] = t5;
  } else {
    t5 = $[32];
  }
  let t6;
  if ($[59] !== t4 || $[60] !== t5) {
    t6 = <ul className="flex touch-pan-y" onDragStart={handleNativeDragStart} {...t4}>{t5}</ul>;
    $[59] = t4;
    $[60] = t5;
    $[61] = t6;
  } else {
    t6 = $[61];
  }
  let t7;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div aria-hidden="true" className={styles.featureOverlay} />;
    $[62] = t7;
  } else {
    t7 = $[62];
  }
  let t8;
  if ($[63] !== canSelectNext || $[64] !== canSelectPrevious || $[65] !== dataGASection || $[66] !== features.length || $[67] !== nextBtnDisabled || $[68] !== onNextButtonClick || $[69] !== onPrevButtonClick || $[70] !== prevBtnDisabled || $[71] !== tracking.featureNext || $[72] !== tracking.featurePrev) {
    t8 = features.length > 1 && <>{!prevBtnDisabled && <div className={cn(FEATURE_NAV_WRAP_CLASS_NAME, "left-6 lg:group-hover:pointer-events-auto lg:group-hover:translate-x-0 lg:group-hover:opacity-100 lg:group-has-focus-visible:pointer-events-auto lg:group-has-focus-visible:translate-x-0 lg:group-has-focus-visible:opacity-100 lg:pointer-fine:-translate-x-3")}><Button type="button" aria-label={SPARTAN_SLIDER_NAV_LABELS.prev} variant="primary" color="white" size="sm" iconBefore={<span className={cn("block size-full", arrowStyles.arrowLeft)}><IconArrowBack /></span>} aria-disabled={!canSelectPrevious} onClick={() => {
          if (!canSelectPrevious) {
            return;
          }
          onPrevButtonClick();
        }} className={cn(SPARTAN_PRODUCT_ICON_BUTTON_CLASS_NAME, arrowStyles.trigger, !canSelectPrevious && "pointer-events-none opacity-40")} {...createDataAttributes({
          test: tracking.featurePrev
        })} {...createDataGA({
          eventName: SPARTAN_PRODUCTS_EVENT_NAME,
          group: dataGASection,
          click: true,
          text: SPARTAN_SLIDER_NAV_LABELS.prev,
          value: "prev"
        })} /></div>}{!nextBtnDisabled && <div className={cn(FEATURE_NAV_WRAP_CLASS_NAME, "right-6 lg:group-hover:pointer-events-auto lg:group-hover:translate-x-0 lg:group-hover:opacity-100 lg:group-has-focus-visible:pointer-events-auto lg:group-has-focus-visible:translate-x-0 lg:group-has-focus-visible:opacity-100 lg:pointer-fine:translate-x-3")}><Button type="button" aria-label={SPARTAN_SLIDER_NAV_LABELS.next} variant="primary" color="white" size="sm" iconBefore={<span className={cn("block size-full", arrowStyles.arrow)}><IconArrowForward /></span>} aria-disabled={!canSelectNext} onClick={() => {
          if (!canSelectNext) {
            return;
          }
          onNextButtonClick();
        }} className={cn(SPARTAN_PRODUCT_ICON_BUTTON_CLASS_NAME, arrowStyles.trigger, !canSelectNext && "pointer-events-none opacity-40")} {...createDataAttributes({
          test: tracking.featureNext
        })} {...createDataGA({
          eventName: SPARTAN_PRODUCTS_EVENT_NAME,
          group: dataGASection,
          click: true,
          text: SPARTAN_SLIDER_NAV_LABELS.next,
          value: "next"
        })} /></div>}</>;
    $[63] = canSelectNext;
    $[64] = canSelectPrevious;
    $[65] = dataGASection;
    $[66] = features.length;
    $[67] = nextBtnDisabled;
    $[68] = onNextButtonClick;
    $[69] = onPrevButtonClick;
    $[70] = prevBtnDisabled;
    $[71] = tracking.featureNext;
    $[72] = tracking.featurePrev;
    $[73] = t8;
  } else {
    t8 = $[73];
  }
  let t9;
  if ($[74] !== emblaRef || $[75] !== t6 || $[76] !== t8) {
    t9 = <div className="rounded-lg" {...t2}><div ref={emblaRef} className={t3} role="region" aria-roledescription="carousel" aria-label={SPARTAN_SLIDER_NAV_LABELS.carousel}>{t6}{t7}{t8}</div></div>;
    $[74] = emblaRef;
    $[75] = t6;
    $[76] = t8;
    $[77] = t9;
  } else {
    t9 = $[77];
  }
  return t9;
};
function _temp(event) {
  event.preventDefault();
}